<script setup lang="ts">
  import { ref, onMounted, inject } from 'vue'
  import { CloudUpload, ChevronRightIcon, CheckCircle, Loader, PencilIcon } from 'lucide-vue-next'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { DocumentsService } from '@/types/generated'
  import { useToast } from '@/components/ui/toast'
  import { genericSuccessToast } from '@/lib/toasts'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useOfficeDocument } from '@/composables/useOfficeDocument'
  import { FlagEnum } from '@/types/generated/types.gen'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import { useRouter } from 'vue-router'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { computed } from 'vue'

  const router = useRouter()
  const store = useCaseStore()
  const { toast } = useToast()

  const officeEditingFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_EDITING)
  const {
    isOfficeFile,
    openOfficeEditor,
    getOfficeFileColors,
    getOfficeFileAriaLabel,
    getOfficeFileType
  } = useOfficeDocument()
  const showUploadModal = ref(false)
  const displayedDocuments = computed(() => {
    // Get all documents from the store (loaded via dedicated endpoint)
    const allDocuments = [...(store.documents || [])]
    // Process and sort documents by date (newest first)
    return allDocuments
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 4)
  })

  // Expose action button for "Dokumente hochladen"
  const actionLabel = 'Dokumente hochladen'
  const actionIcon = CloudUpload
  const handleAction = () => {
    showUploadModal.value = true
  }
  defineExpose({
    actionLabel,
    actionIcon,
    handleAction
  })

  onMounted(async () => {
    await officeEditingFlag.checkFlag()
  })

  const handleFileUploaded = async (documentUuid: string) => {
    showUploadModal.value = false
    genericSuccessToast()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Function to get the status icon and text
  const getDocumentStatus = (doc: any) => {
    if (['OPEN', 'PENDING'].includes(doc.status)) {
      return {
        icon: Loader,
        label: 'Angefordert',
        class:
          'text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1 inline-flex items-center gap-1.5'
      }
    }
    return {
      icon: CheckCircle,
      label: 'Erhalten',
      class:
        'text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-1 inline-flex items-center gap-1.5'
    }
  }

  // Navigate to full documents view
  const viewAllDocuments = () => {
    if (store.caseRecord?.uuid) {
      router.push(`/app/cases/${store.caseRecord.uuid}/documents`)
    }
  }

  const registerWidgetActionHandler = inject('registerWidgetActionHandler')
  onMounted(() => {
    if (registerWidgetActionHandler) {
      registerWidgetActionHandler('DOCUMENTS', () => {
        showUploadModal.value = true
      })
    }
  })

  const openDocument = async (document: any) => {
    if (officeEditingFlag.isEnabled.value && isOfficeFile(document)) {
      openOfficeEditor(document)
      return
    }

    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        window.open(response.url, '_blank')
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Dokument konnte nicht geöffnet werden'
        })
      }
    } catch (error) {
      console.error('Error opening document:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Dokument konnte nicht geöffnet werden'
      })
    }
  }
</script>

<template>
  <div class="documents-widget">
    <!-- Loading State -->
    <div
      v-if="store.isLoadingDocuments"
      class="space-y-3"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="animate-pulse rounded-lg border border-gray-200 bg-gray-50 p-3"
      >
        <div class="h-4 w-3/4 rounded bg-gray-200"></div>
      </div>
    </div>

    <!-- No Documents State -->
    <div
      v-else-if="!store.isLoadingDocuments && displayedDocuments.length === 0"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="40"
          height="40"
          rx="8"
          fill="#F3E8FF"
        />
        <path
          d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
          stroke="#7E22CE"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="text-md mt-5 text-center text-gray-500">Keine Dokumente vorhanden</p>
    </div>

    <!-- Documents List -->
    <div
      v-else
      class="space-y-2"
    >
      <div
        v-for="document in displayedDocuments"
        :key="document.uuid"
        class="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-2"
      >
        <!-- Document name with Office indicator -->
        <div class="mr-4 min-w-0 flex-shrink">
          <div class="flex items-center gap-2">
            <!-- Office file type badge - responsive -->
            <div
              v-if="officeEditingFlag.isEnabled && isOfficeFile(document)"
              class="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium sm:gap-1.5
                sm:px-2"
              :class="[
                getOfficeFileColors(document.filename).bg,
                getOfficeFileColors(document.filename).text,
                getOfficeFileColors(document.filename).border
              ]"
              :title="getOfficeFileType(document.filename) + ' (Editable)'"
              :aria-label="getOfficeFileAriaLabel(document.filename, true)"
            >
              <PencilIcon class="h-2.5 w-2.5 sm:h-3 sm:w-3" />
              <span class="xs:inline hidden sm:inline">
                {{ getOfficeFileType(document.filename).split(' ')[0] }}
              </span>
            </div>

            <!-- Document name -->
            <div
              class="cursor-pointer truncate text-base hover:text-purple-600 hover:underline"
              :class="
                document.document_type === 'Fragebogen'
                  ? 'font-medium text-gray-800'
                  : 'font-medium text-gray-800'
              "
              :aria-label="
                getOfficeFileAriaLabel(
                  document.filename,
                  officeEditingFlag.isEnabled && isOfficeFile(document)
                )
              "
              @click="openDocument(document)"
            >
              {{ document.filename.replace('.pdf', '') }}
            </div>
          </div>
        </div>

        <!-- Status badge and date -->
        <div class="flex flex-shrink-0 items-center gap-4">
          <div :class="getDocumentStatus(document).class">
            <component
              :is="getDocumentStatus(document).icon"
              class="h-4 w-4"
            />
            {{ getDocumentStatus(document).label }}
          </div>
          <div class="text-sm text-gray-800">
            {{ formatDate(document.created_at) }}
          </div>
        </div>
      </div>
    </div>

    <!-- View All Link -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewAllDocuments"
      >
        Alle Dokumente ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>

    <!-- Upload Document Modal -->
    <Dialog
      :open="showUploadModal"
      @update:open="showUploadModal = false"
    >
      <DialogContent class="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dokumente hochladen</DialogTitle>
        </DialogHeader>
        <div class="py-4">
          <DocumentUpload
            v-if="store.caseRecord"
            :case-data="store.caseRecord"
            :auto-remove-after-upload="false"
            @file-uploaded="handleFileUploaded"
          />
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>
  .documents-widget {
    width: 100%;
    box-sizing: border-box;
  }
</style>
